import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ScreenGuard } from "@/components/rbac/ScreenGuard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateRole,
  useRole,
  useRoles,
  useUpdateRole,
} from "@/hooks/useRbac";
import { useAuth } from "@/contexts/AuthContext";
import {
  ACTION_LABELS,
  ACTION_TO_FLAG,
  MODULE_ACTIONS,
  MODULE_LABELS,
  RBAC_MODULES,
  RbacAction,
  RbacModule,
} from "@/rbac/modules";
import { RolePermissionInput } from "@/hooks/useRbac";

type PermsState = Record<RbacModule, Partial<Record<RbacAction, boolean>>>;

const emptyPerms = (): PermsState => {
  const out = {} as PermsState;
  for (const m of RBAC_MODULES) out[m] = {};
  return out;
};

const toInputs = (perms: PermsState): RolePermissionInput[] =>
  (Object.keys(perms) as RbacModule[]).map((module) => {
    const row: RolePermissionInput = { module };
    for (const a of MODULE_ACTIONS[module]) {
      row[ACTION_TO_FLAG[a]] = !!perms[module][a];
    }
    return row;
  });

const fromApi = (
  source?: Record<string, Record<string, boolean>>,
): PermsState => {
  const out = emptyPerms();
  if (!source) return out;
  for (const m of RBAC_MODULES) {
    const modSrc = source[m];
    if (!modSrc) continue;
    for (const a of MODULE_ACTIONS[m]) {
      // Backend returns both the action name (from resolvePermissions) and
      // the flag name (canView etc, from serializeRole). Support both.
      out[m][a] = Boolean(modSrc[a] ?? modSrc[ACTION_TO_FLAG[a]]);
    }
  }
  return out;
};

export const RoleEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const { rbac } = useAuth();

  const { data: allRoles = [] } = useRoles();
  const { data: current } = useRole(isNew ? null : id!);
  const createMut = useCreateRole();
  const updateMut = useUpdateRole();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [copyFrom, setCopyFrom] = useState<string>("");
  const [perms, setPerms] = useState<PermsState>(emptyPerms);

  useEffect(() => {
    if (current && !isNew) {
      setName(current.name);
      setDescription(current.description ?? "");
      setPerms(fromApi(current.permissions));
    }
  }, [current, isNew]);

  useEffect(() => {
    if (!isNew || !copyFrom) return;
    const src = allRoles.find((r) => r.id === copyFrom);
    if (src) setPerms(fromApi(src.permissions));
  }, [copyFrom, allRoles, isNew]);

  const isEditingOwnRole = !isNew && rbac?.roleId === id;
  const isSystem = !!current?.isSystemRole;
  const memberCount = current?.memberCount ?? 0;

  const toggle = (m: RbacModule, a: RbacAction) =>
    setPerms((prev) => ({ ...prev, [m]: { ...prev[m], [a]: !prev[m][a] } }));

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Role name is required");
      return;
    }
    if (isEditingOwnRole) {
      toast.error("You cannot edit your own role");
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      permissions: toInputs(perms),
    };

    if (isNew) {
      createMut.mutate(
        { ...payload, copyFromRoleId: copyFrom || undefined },
        {
          onSuccess: () => {
            toast.success("Role created");
            navigate("/roles");
          },
          onError: (e) => toast.error(e.message),
        },
      );
      return;
    }

    updateMut.mutate(
      { id: id!, data: payload },
      {
        onSuccess: () => {
          toast.success("Role saved");
          navigate("/roles");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const title = isNew ? "New role" : `Edit: ${current?.name ?? ""}`;

  const copyOptions = useMemo(
    () => allRoles.filter((r) => r.id !== id),
    [allRoles, id],
  );

  return (
    <ScreenGuard module="staffManagement">
      <AppLayout title={title}>
        <button
          type="button"
          onClick={() => navigate("/roles")}
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to roles
        </button>

        <div className="space-y-4 rounded-card border border-border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="role-name">Role name</Label>
              <Input
                id="role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSystem}
              />
              {isSystem && (
                <p className="mt-1 text-xs text-muted-foreground">
                  System role names are locked.
                </p>
              )}
            </div>
            {isNew && (
              <div>
                <Label>Copy permissions from</Label>
                <Select value={copyFrom} onValueChange={setCopyFrom}>
                  <SelectTrigger>
                    <SelectValue placeholder="Start from scratch" />
                  </SelectTrigger>
                  <SelectContent>
                    {copyOptions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="role-desc">Description</Label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this role do?"
            />
          </div>

          {memberCount > 0 && !isNew && (
            <div className="flex items-start gap-2 rounded-btn border border-warning/40 bg-warning/5 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-warning" />
              <div>
                <strong>{memberCount}</strong> staff currently hold this role.
                Saving will immediately change what they can do and invalidate
                their sessions.
              </div>
            </div>
          )}

          {isEditingOwnRole && (
            <div className="rounded-btn border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              You&apos;re assigned to this role and can&apos;t edit your own
              permissions.
            </div>
          )}
        </div>

        <div className="mt-5 overflow-hidden rounded-card border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Module</th>
                <th className="px-4 py-2 text-left font-medium">Permissions</th>
              </tr>
            </thead>
            <tbody>
              {RBAC_MODULES.map((mod) => (
                <tr key={mod} className="border-t border-border">
                  <td className="px-4 py-3 align-top font-medium">
                    {MODULE_LABELS[mod]}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-4">
                      {MODULE_ACTIONS[mod].map((a) => (
                        <label
                          key={a}
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                        >
                          <Switch
                            checked={!!perms[mod][a]}
                            onCheckedChange={() => toggle(mod, a)}
                            disabled={isEditingOwnRole}
                          />
                          {ACTION_LABELS[a]}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate("/roles")}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createMut.isPending || updateMut.isPending || isEditingOwnRole
            }
          >
            {isNew ? "Create role" : "Save changes"}
          </Button>
        </div>
      </AppLayout>
    </ScreenGuard>
  );
};

export default RoleEditor;
